import { ConditionnementDoc } from '../components/stocks/ConditionnementDoc';
import { SituationClientRetrait } from '../components/stocks/SituationClientRetrait';
import { RegistreTracabiliteCuves } from '../components/tracabilite/RegistreTracabiliteCuves';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Lock, Plus, ArrowRightLeft, Trash2, PackageMinus, PackagePlus, FileText } from '../lib/icons';
import { toast } from 'sonner';
import { fetchCuves, deleteCuve, fetchRelevesClients, enregistrerRetrait, fetchFormats, fetchProduitsFinis, fetchConditionnements, fetchBonus, fetchTracabiliteCuves, stockFinancier, stockAchat, valeurMatiere, fetchTransferts } from '../lib/dataProvider';
import { ApproEmballages } from '../components/stocks/ApproEmballages';
import { majPrixVenteSku } from '../lib/dataProvider';
import { fmt, PageTitle } from '../components/shared/badges';
import { couleurGain, couleurSolde } from '../lib/couleurs';
import { RisqueHuile } from '../components/stocks/RisqueHuile';
import { CuveForm } from '../components/stocks/CuveForm';
import { TransfertDialog } from '../components/stocks/TransfertDialog';
import { TransfertsHistorique } from '../components/stocks/TransfertsHistorique';
import { OrdreTransfertDoc } from '../components/stocks/OrdreTransfertDoc';
import { ConditionnementDialog } from '../components/stocks/ConditionnementDialog';
import { ReleveCuve } from '../components/stocks/ReleveCuve';
import { BonSortieHuile } from '../components/releves/BonSortieHuile';
import { useAuth } from '../lib/auth';
import { voitMontants } from '../lib/permissions';

const FILTRES = [
  { id: 'tous', label: 'Toutes les cuves' },
  { id: 'noble', label: 'Nobles (Vierge / Extra)' },
  { id: 'lampante', label: 'Lampantes' },
];

const CuveTable = ({ cuves, famille, onTransfert, onDelete, onPrelevement, onReleve }) => {
  const { user } = useAuth();
  const argent = voitMontants(user?.role);
  const lamp = famille === 'lampante';
  const volume = cuves.reduce((s, c) => s + c.volume, 0);
  const volumeClients = cuves.reduce((s, c) => s + (c.volumeClients || 0), 0);
  const financier = cuves.reduce((s, c) => s + stockFinancier(c), 0);
  const capacite = cuves.reduce((s, c) => s + c.capacite, 0);
  const valeur = cuves.reduce((s, c) => s + valeurMatiere(c), 0);
  // Part d'huile d'origine repassage encore présente dans les cuves (coût matière nul).
  const repassage = cuves.reduce((s, c) => s + (c.volumeRepassage || 0), 0);

  return (
    <section
      data-testid={`stock-bloc-${famille}`}
      className={`border border-slate-200 border-l-2 bg-white ${lamp ? 'border-l-orange-500' : 'border-l-emerald-600'}`}
    >
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 px-4 py-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
            {lamp && <Lock size={14} className="text-orange-600" />}
            {lamp ? 'Cuves Lampantes — accès cloisonné' : 'Cuves Vierge / Extra-Vierge'}
          </h2>
          <p className="text-xs text-slate-500">
            {lamp
              ? 'Jamais mélangées ni cumulées aux huiles nobles'
              : 'Huiles nobles — incluses dans les bilans globaux'}
          </p>
        </div>
        <div className="flex gap-6 text-right">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Stock physique</p>
            <p data-testid={`stock-total-${famille}`} className="tabular font-data text-2xl font-medium text-slate-900">
              {fmt(volume)}
            </p>
            <p className="font-data text-[11px] text-slate-500">
              reste {fmt(capacite - volume)} / {fmt(capacite)}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-sky-700">Dette clients (nature)</p>
            <p data-testid={`stock-clients-${famille}`} className="tabular font-data text-2xl font-medium text-sky-800">
              {fmt(volumeClients)}
            </p>
            <p className="font-data text-[11px] text-slate-500">kg hors CMP</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Stock financier</p>
            <p data-testid={`stock-financier-${famille}`} className={`tabular font-data text-2xl font-medium ${couleurSolde(financier)}`}>
              {fmt(financier)}
            </p>
            <p className="font-data text-[11px] text-slate-500">{financier < 0 ? 'emprunt sur stock client' : 'propriété huilerie'}</p>
          </div>
          {lamp && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-orange-700">Dont repassage</p>
              <p data-testid={`stock-repassage-${famille}`} className="tabular font-data text-2xl font-medium text-orange-800">
                {fmt(repassage)}
              </p>
              <p className="font-data text-[11px] text-slate-500">kg à coût matière 0</p>
            </div>
          )}
          {argent && (
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Valeur au CMP</p>
            <p data-testid={`stock-valeur-${famille}`} className="tabular font-data text-2xl font-medium text-slate-900">
              {fmt(valeur)}
            </p>
            <p className="font-data text-[11px] text-slate-500">TND (stock financier)</p>
          </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left" data-testid={`stock-table-${famille}`}>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {['Désignation', 'Qualité', 'Propriété', 'Emplacement', 'Capacité max', 'Stock physique', 'Dont clients', ...(lamp ? ['Dont repassage'] : []), 'Stock financier', 'Reste dispo.', ...(argent ? ['CMP (TND)'] : []), 'Remplissage', ''].map((c) => (
                <th key={c} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cuves.length === 0 ? (
              <tr data-testid={`stock-empty-${famille}`}>
                <td colSpan={12} className="px-3 py-10 text-center text-sm text-slate-500">
                  Aucune cuve configurée pour cette filière.
                </td>
              </tr>
            ) : (
              cuves.map((c) => {
                const pct = c.capacite ? (c.volume / c.capacite) * 100 : 0;
                const alerte = pct >= 90;
                return (
                  <tr key={c.id} data-testid={`cuve-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{c.nom}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{c.qualite}</td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`cuve-proprietaire-${c.id}`}
                        className={`font-data text-[11px] uppercase tracking-[0.06em] ${
                          c.proprietaire === 'clients' ? 'text-sky-700' : 'text-slate-600'
                        }`}
                      >
                        {c.proprietaire === 'clients' ? 'Compte clients' : 'Huilerie'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{c.emplacement}</td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-600">
                      {fmt(c.capacite)} {c.unite}
                    </td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">
                      {fmt(c.volume)} {c.unite}
                    </td>
                    <td data-testid={`cuve-clients-${c.id}`} className="tabular px-3 py-2 font-data text-sm text-sky-800">
                      {fmt(c.volumeClients || 0)}
                    </td>
                    {lamp && (
                      <td data-testid={`cuve-part-repassage-${c.id}`} className="tabular px-3 py-2 font-data text-sm text-orange-800">
                        {(c.volumeRepassage || 0) > 0 ? (
                          <>
                            {fmt(c.volumeRepassage, 3)}
                            <span className="ml-1 text-[10px] text-slate-500">
                              {c.volume ? `${fmt(((c.volumeRepassage || 0) / c.volume) * 100, 1)} %` : ''}
                            </span>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    )}
                    <td data-testid={`cuve-financier-${c.id}`} className={`tabular px-3 py-2 font-data text-sm font-semibold ${couleurSolde(stockFinancier(c))}`}>
                      {fmt(stockFinancier(c))}
                    </td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-900">
                      {fmt(c.capacite - c.volume)} {c.unite}
                    </td>
                    {argent && (
                      <td className="tabular px-3 py-2 font-data text-sm text-slate-900">
                        {stockAchat(c) > 0 && c.cmp ? (
                          fmt(c.cmp, 2)
                        ) : (
                          <span data-testid={`cmp-aucun-achat-${c.id}`} className="text-[11px] text-slate-500">
                            {(c.volumeRepassage || 0) > 0 ? 'Aucun achat' : '—'}
                          </span>
                        )}
                        {(c.volumeRepassage || 0) > 0 && (
                          <span data-testid={`cuve-repassage-${c.id}`} className="mt-0.5 block border border-orange-300 bg-orange-50 px-1 font-data text-[10px] font-bold text-orange-800">
                            repassage · {fmt(c.volumeRepassage, 3)} kg à coût matière 0
                          </span>
                        )}
                        {(c.volumeProvisoire || 0) > 0 && (
                          <span data-testid={`cmp-provisoire-${c.id}`} className="mt-0.5 block border border-amber-400 bg-amber-50 px-1 font-data text-[10px] font-bold text-amber-800">
                            provisoire · {fmt(c.volumeProvisoire, 3)} kg à prix non fixé
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-24 bg-slate-100">
                          <div
                            className={`h-2 ${alerte ? 'bg-red-600' : lamp ? 'bg-orange-500' : 'bg-emerald-600'}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <span className="tabular font-data text-xs text-slate-600">{fmt(pct, 1)} %</span>
                        {alerte && (
                          <span data-testid={`cuve-alerte-${c.id}`} className="inline-flex items-center gap-1 border border-red-200 bg-red-50 px-1.5 py-0.5 font-data text-[10px] uppercase text-red-700">
                            <AlertTriangle size={11} /> Saturation
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {!c.virtuelle && !c.verrouillee && (
                          <button
                            type="button"
                            data-testid={`cuve-prelevement-${c.id}`}
                            title="Prélèvement client"
                            onClick={() => onPrelevement(c)}
                            className="border border-sky-300 bg-sky-50 p-1.5 text-sky-700 transition-colors duration-150 hover:bg-sky-700 hover:text-white"
                          >
                            <PackageMinus size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          data-testid={`cuve-releve-${c.id}`}
                          title="Relevé de compte d’huile"
                          onClick={() => onReleve(c)}
                          className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          <FileText size={13} />
                        </button>
                        <button
                          type="button"
                          data-testid={`cuve-transfert-${c.id}`}
                          title="Transférer"
                          onClick={() => onTransfert(c.id)}
                          className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                        >
                          <ArrowRightLeft size={13} />
                        </button>
                        <button
                          type="button"
                          data-testid={`cuve-supprimer-${c.id}`}
                          title="Supprimer"
                          onClick={() => onDelete(c)}
                          className="border border-slate-300 p-1.5 text-slate-600 transition-colors duration-150 hover:bg-red-600 hover:text-white"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default function Stocks() {
  const { user } = useAuth();
  const argent = voitMontants(user?.role);
  const [cuves, setCuves] = useState([]);
  const [filtre, setFiltre] = useState('tous');
  const [formOpen, setFormOpen] = useState(false);
  const [transfertSource, setTransfertSource] = useState(null);
  const [transferts, setTransferts] = useState([]);
  const [ordreImprime, setOrdreImprime] = useState(null);
  const [releves, setReleves] = useState([]);
  const [prelevement, setPrelevement] = useState(null);
  const [releve, setReleve] = useState(null);
  const [recu, setRecu] = useState(null);
  const [ficheConditionnement, setFicheConditionnement] = useState(null);
  const [conditionnement, setConditionnement] = useState(false);
  const [formats, setFormats] = useState([]);
  const [skus, setSkus] = useState([]);
  const [appro, setAppro] = useState(false);
  const [conds, setConds] = useState([]);
  const [bonus, setBonus] = useState({ volume: 0, noble: 0, lampante: 0, valeur: 0, valeurEstimee: 0, valeurRealisee: 0, kgVendu: 0, mouvements: [] });
  const [trace, setTrace] = useState({});

  const ouvrirPrelevement = (cuve) => setPrelevement({ cuve, client: '', quantite: '' });

  const validerPrelevement = async (e) => {
    e.preventDefault();
    const { error, row } = await enregistrerRetrait({
      date: new Date().toISOString().slice(0, 10),
      client: prelevement.client,
      cuveId: prelevement.cuve.id,
      litres: prelevement.quantite,
      notes: prelevement.notes,
      utilisateur: user?.name,
    });
    if (error) return toast.error(error);
    const majReleves = await fetchRelevesClients();
    setReleves(majReleves);
    setRecu({
      ...row,
      cuveNom: prelevement.cuve.nom,
      qualite: prelevement.cuve.qualite || (prelevement.cuve.famille === 'lampante' ? 'Lampante' : 'Vierge'),
      solde: majReleves.find((r) => r.nom === prelevement.client)?.solde ?? 0,
    });
    toast.success(`Bon de sortie d’huile généré — ${fmt(row.litres, 1)} kg`);
    setPrelevement(null);
    load();
  };

  const load = useCallback(() => {
    fetchCuves().then(setCuves);
    fetchRelevesClients().then(setReleves);
    fetchFormats().then(setFormats);
    fetchProduitsFinis().then(setSkus);
    fetchConditionnements().then(setConds);
    fetchBonus().then(setBonus);
    fetchTracabiliteCuves().then(setTrace);
    fetchTransferts().then(setTransferts);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const noble = useMemo(() => cuves.filter((c) => c.famille === 'noble'), [cuves]);
  const lampante = useMemo(() => cuves.filter((c) => c.famille === 'lampante'), [cuves]);

  const supprimer = async (c) => {
    if (c.volume > 0) return toast.error(`« ${c.nom} » contient encore du stock — vidangez-la d'abord.`);
    await deleteCuve(c.id);
    toast.success(`Cuve « ${c.nom} » supprimée`);
    load();
  };

  return (
    <div data-testid="stocks-page" className="space-y-6">
      <RegistreTracabiliteCuves />
      <PageTitle
        emoji="🛢️"
        titre="Stocks & Cuves (Pile Huile)"
        sous="Désignation, capacité maximale, quantité actuelle, reste disponible et coût moyen pondéré"
        action={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="open-conditionnement"
              onClick={() => setConditionnement(true)}
              className="flex h-11 items-center gap-2 border-2 border-slate-900 px-4 font-heading text-sm font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10"
            >
              <PackagePlus size={15} /> Mise en bouteille
            </button>
            <button
              type="button"
              data-testid="open-transfert-dialog"
              onClick={() => setTransfertSource('')}
              className="flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-10"
            >
              <ArrowRightLeft size={15} /> Ordre de transfert
            </button>
            <button
              type="button"
              data-testid="open-cuve-form"
              onClick={() => setFormOpen(true)}
              className="flex h-11 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700 sm:h-10"
            >
              <Plus size={16} /> Ajouter une cuve / pile
            </button>
          </div>
        }
      />

      <div data-testid="etancheite-alerte" className="flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3">
        <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-red-600" />
        <p className="text-xs text-red-800">
          <span className="font-bold uppercase tracking-[0.06em]">Étanchéité verrouillée</span> — toute
          affectation croisée entre une cuve d'huile noble et une cuve lampante est refusée par le
          système. Les deux filières sont stockées et valorisées séparément.
        </p>
      </div>

      <RisqueHuile />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-px bg-slate-200">
          {FILTRES.map((f) => (
            <button
              key={f.id}
              type="button"
              data-testid={`filtre-cuve-${f.id}`}
              onClick={() => setFiltre(f.id)}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                filtre === f.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
          {cuves.length} cuve{cuves.length === 1 ? '' : 's'} configurée{cuves.length === 1 ? '' : 's'}
        </p>
      </div>

      {(filtre === 'tous' || filtre === 'noble') && (
        <CuveTable cuves={noble} famille="noble" onTransfert={setTransfertSource} onDelete={supprimer} onPrelevement={ouvrirPrelevement} onReleve={setReleve} />
      )}
      {(filtre === 'tous' || filtre === 'lampante') && (
        <CuveTable cuves={lampante} famille="lampante" onTransfert={setTransfertSource} onDelete={supprimer} onPrelevement={ouvrirPrelevement} onReleve={setReleve} />
      )}

      <TransfertsHistorique transferts={transferts} onDone={load} onImprimer={setOrdreImprime} />

      <section data-testid="cuve-bonus" className="border border-slate-200 border-l-2 border-l-amber-500 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">
              Bonus &amp; Garde — Huilerie <span className="font-data text-[11px] uppercase tracking-[0.08em] text-amber-700">cuve virtuelle</span>
            </h2>
            <p className="text-xs text-slate-500">
              Retenue automatique de 2 % sur les achats au rendement, à prix fixe et les prestations
              stockées à l'huilerie — {bonus.mouvements.length} mouvement(s)
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-amber-700">Solde bonus</p>
            <p data-testid="bonus-volume" className="tabular font-data text-2xl font-medium text-slate-900">
              {fmt(bonus.volume, 3)} <span className="text-sm text-slate-500">Kg</span>
            </p>
            {argent && (
              <p data-testid="bonus-valeur" className="font-data text-[11px] text-slate-600">
                Valeur estimée : {fmt(bonus.valeurEstimee ?? 0, 3)} TND
                <span className="ml-1 text-slate-400">
                  (dernier prix de vente : {fmt(bonus.prixNoble ?? 0, 3)} TND/kg nobles · {fmt(bonus.prixLampante ?? 0, 3)} lampante)
                </span>
              </p>
            )}
            <p className="font-data text-[11px] text-slate-500">
              Nobles {fmt(bonus.noble, 3)} · Lampante {fmt(bonus.lampante, 3)} Kg
            </p>
            {argent && (
              <p data-testid="bonus-realise" className="font-data text-[11px] text-emerald-700">
                Valeur réalisée : {fmt(bonus.valeurRealisee ?? 0, 3)} TND ({fmt(bonus.kgVendu ?? 0, 3)} Kg vendus)
              </p>
            )}
          </div>
        </div>
        {bonus.mouvements.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left" data-testid="table-bonus">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Date', 'Lots / vente', 'Filière', 'Huile (Kg)', 'Valeur réalisée (TND)', 'Motif'].map((c) => (
                    <th key={c} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bonus.mouvements.slice(0, 10).map((m) => (
                  <tr key={m.id} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{m.date}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-900">{m.lots.join(', ')}</td>
                    <td className="px-3 py-2 font-data text-xs uppercase text-slate-600">{m.famille}</td>
                    <td className={`tabular px-3 py-2 font-data text-sm font-semibold ${m.kg < 0 ? 'text-red-700' : 'text-amber-800'}`}>
                      {m.kg < 0 ? '' : '+'}{fmt(m.kg, 3)}
                    </td>
                    <td className="tabular px-3 py-2 font-data text-sm text-emerald-700" data-testid={`bonus-valeur-${m.id}`}>
                      {m.realise ? fmt(m.realise, 3) : '—'}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600">{m.motif}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section data-testid="tracabilite-cuves" className="border border-slate-200 border-l-2 border-l-slate-900 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">
            Traçabilité lots → cuves
          </h2>
          <p className="text-xs text-slate-500">
            Lots ayant alimenté chaque cuve, avec les quantités versées et la retenue appliquée
          </p>
        </div>
        {Object.keys(trace).length === 0 ? (
          <p data-testid="tracabilite-vide" className="px-4 py-8 text-center text-sm text-slate-500">
            Aucun versement enregistré : déclarez une sortie d'huile depuis la Trituration.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {cuves
              .filter((c) => trace[c.id]?.length)
              .map((c) => (
                <div key={c.id} data-testid={`trace-cuve-${c.id}`} className="px-4 py-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-heading text-sm font-bold text-slate-900">
                      {c.nom}{' '}
                      <span className="font-data text-[10px] uppercase tracking-[0.08em] text-slate-500">
                        {c.famille === 'lampante' ? 'lampante' : 'noble'}
                      </span>
                    </p>
                    <p className="font-data text-xs text-slate-600">
                      {trace[c.id].length} lot(s) · {fmt(trace[c.id].reduce((s, l) => s + l.kg, 0), 3)} Kg versés
                    </p>
                  </div>
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                      <thead>
                        <tr className="bg-slate-50">
                          {['Lot', 'Date', 'Tiers', 'Parcours', 'Versé (Kg)', 'Retenue (Kg)'].map((h) => (
                            <th key={h} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {trace[c.id].map((l, i) => (
                          <tr key={`${l.lot}-${i}`} className="border-b border-slate-100">
                            <td className="px-3 py-1.5 font-data text-sm font-semibold text-slate-900">{l.lot}</td>
                            <td className="px-3 py-1.5 font-data text-sm text-slate-700">{l.date}</td>
                            <td className="px-3 py-1.5 text-sm text-slate-800">{l.tiers}</td>
                            <td className="px-3 py-1.5 font-data text-xs uppercase text-slate-600">
                              {l.parcours === 'client' ? 'prestation' : 'achat'}
                            </td>
                            <td className="tabular px-3 py-1.5 font-data text-sm font-semibold text-slate-900">{fmt(l.kg, 3)}</td>
                            <td className="tabular px-3 py-1.5 font-data text-sm text-amber-800">{l.retenue ? fmt(l.retenue, 3) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
          </div>
        )}
      </section>

      <section data-testid="produits-finis-section" className="border border-slate-200 border-l-2 border-l-sky-600 bg-white">
        <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">
              Produits finis en rayon (SKU)
            </h2>
            <p className="text-xs text-slate-500">
              Format + dénomination, disponibles pour la vente au détail — {conds.length} opération(s) de mise en bouteille
            </p>
          </div>
          <p className="font-data text-sm font-semibold text-slate-900">
            {fmt(skus.reduce((s, x) => s + x.stock, 0))} unités · {fmt(skus.length)} référence(s)
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-produits-finis">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['SKU', 'Format', 'Dénomination', 'Contenance (L)', ...(argent ? ['Coût unitaire (TND)', 'Prix de vente (TND/u)', 'Marge (TND/u)'] : []), 'Stock (unités)', 'Équivalent huile (kg)'].map((c) => (
                  <th key={c} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {skus.length === 0 ? (
                <tr data-testid="sku-empty">
                  <td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500">
                    Aucun produit fini : lancez un conditionnement depuis une cuve.
                  </td>
                </tr>
              ) : (
                skus.map((x) => (
                  <tr key={x.id} data-testid={`produit-fini-${x.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-xs text-slate-500">{x.id}</td>
                    <td className="px-3 py-2 text-sm font-semibold text-slate-900">{x.formatNom}</td>
                    <td className="px-3 py-2 text-sm text-slate-700">{x.denomination}</td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(x.litres, 2)}</td>
                    {argent && <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(x.cout, 3)}</td>}
                    {argent && (
                      <td className="px-3 py-2">
                        <input
                          data-testid={`prix-vente-${x.id}`}
                          type="number"
                          step="0.001"
                          min="0"
                          defaultValue={x.prixVente ?? 0}
                          onBlur={async (e) => {
                            const { error } = await majPrixVenteSku(x.id, e.target.value);
                            if (error) return toast.error(error);
                            toast.success(`Prix de vente de « ${x.nom} » mis à jour`);
                            load();
                          }}
                          className="tabular h-9 w-28 border border-slate-300 bg-slate-50 px-2 text-right font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                        />
                      </td>
                    )}
                    {argent && (
                      <td data-testid={`marge-${x.id}`} className={`tabular px-3 py-2 font-data text-sm font-semibold ${couleurGain((x.prixVente ?? 0) - x.cout)}`}>
                        {fmt((x.prixVente ?? 0) - x.cout, 3)}
                      </td>
                    )}
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(x.stock)}</td>
                    <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(x.stock * x.litres * 0.916, 2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section data-testid="emballages-vides-section" className="border border-slate-200 border-l-2 border-l-amber-600 bg-white">
        <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">Emballages vides (consommables)</h2>
            <p className="text-xs text-slate-500">
              Bouteilles et bidons vides achetés aux fournisseurs — consommés à chaque conditionnement
            </p>
          </div>
          <button
            type="button"
            data-testid="ouvrir-appro-emballages"
            onClick={() => setAppro((v) => !v)}
            className="flex h-10 items-center gap-2 border-2 border-slate-900 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            {appro ? 'Masquer l’approvisionnement' : 'Approvisionnement emballages'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-emballages-vides">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Format', 'Contenance (L)', ...(argent ? ['Prix unitaire (TND)'] : []), 'Stock vides (unités)'].map((c) => (
                  <th key={c} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formats.map((f) => (
                <tr key={f.id} data-testid={`emballage-vide-${f.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 text-sm font-semibold text-slate-900">{f.nom}</td>
                  <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(f.litres, 2)}</td>
                  {argent && <td className="tabular px-3 py-2 font-data text-sm text-slate-700">{fmt(f.prixEmballage, 3)}</td>}
                  <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(f.stock)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {appro && (
          <div className="border-t border-slate-200 px-4 py-4">
            <ApproEmballages onDone={load} />
          </div>
        )}
      </section>

      <details className="border border-slate-200 bg-white p-4" data-testid="conditionnements-fiches">
        <summary className="cursor-pointer font-bold">Fiches de conditionnement — réimpression ({conds.length})</summary>
        {conds.length === 0 && <p className="mt-2 text-sm">Aucun conditionnement enregistré.</p>}
        {conds.map(c => <div key={c.id} className="mt-2 flex items-center justify-between gap-3 border-t pt-2 text-sm"><span>{c.date} · {c.id} · {c.cuveNom} · {fmt(c.unites)} unités</span><button type="button" onClick={() => setFicheConditionnement(c)} className="border px-3 py-2">Imprimer</button></div>)}
      </details>
      {prelevement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="prelevement-dialog">
          <form
            onSubmit={validerPrelevement}
            className="w-full max-w-md border-2 border-slate-900 bg-white"
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">
                Prélèvement client — {prelevement.cuve.nom}
              </h2>
              <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
                Consommation personnelle — sans facture de vente
              </p>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Client</label>
                <select
                  data-testid="prelevement-client"
                  value={prelevement.client}
                  onChange={(e) => setPrelevement((p) => ({ ...p, client: e.target.value }))}
                  className="mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                >
                  <option value="">— Choisir —</option>
                  {releves.map((r) => (
                    <option key={r.nom} value={r.nom}>
                      {r.nom} ({fmt(r.solde, 1)} kg)
                    </option>
                  ))}
                </select>
              </div>
              {prelevement.client && <SituationClientRetrait tiersId={releves.find(r=>r.nom===prelevement.client)?.tiersId} />}
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                  Quantité prélevée (kg)
                </label>
                <input
                  data-testid="prelevement-quantite"
                  type="number"
                  step="0.1"
                  value={prelevement.quantite}
                  onChange={(e) => setPrelevement((p) => ({ ...p, quantite: e.target.value }))}
                  className="mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
                <p className="mt-1.5 font-data text-[11px] text-slate-500">
                  Disponible en cuve : {fmt(prelevement.cuve.volume)} kg
                </p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Note / conditions du retrait</label>
                <input
                  data-testid="prelevement-notes"
                  value={prelevement.notes ?? ''}
                  placeholder="Ex. : retiré par son fils, 2 bidons de 20 L, reste en dépôt"
                  onChange={(e) => setPrelevement((p) => ({ ...p, notes: e.target.value }))}
                  className="mt-1.5 h-10 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                />
              </div>
            </div>
            <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
              <button
                type="submit"
                data-testid="prelevement-valider"
                className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
              >
                Valider & bon de sortie d’huile
              </button>
              <button
                type="button"
                data-testid="prelevement-annuler"
                onClick={() => setPrelevement(null)}
                className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {recu && <BonSortieHuile retrait={recu} onClose={() => setRecu(null)} />}

      {formOpen && <CuveForm onClose={() => setFormOpen(false)} onCreated={load} />}
      {ficheConditionnement && <ConditionnementDoc row={ficheConditionnement} onClose={() => setFicheConditionnement(null)} />}
      {conditionnement && (
        <ConditionnementDialog cuves={cuves} onClose={() => setConditionnement(false)} onDone={load} />
      )}
      {releve && <ReleveCuve cuve={releve} onClose={() => setReleve(null)} />}
      {transfertSource !== null && (
        <TransfertDialog
          cuves={cuves}
          sourceId={transfertSource}
          onClose={() => setTransfertSource(null)}
          onDone={(ordre) => {
            load();
            setOrdreImprime(ordre);
          }}
        />
      )}
      {ordreImprime && <OrdreTransfertDoc ordre={ordreImprime} onClose={() => setOrdreImprime(null)} />}
    </div>
  );
}
